import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DownOutlined, FullscreenExitOutlined, FullscreenOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import './InstrumentDetails.css';
import apiClient from '../../services/apiClient';

const defaultTermsheetUrl =
  'https://nsearchives.nseindia.com/content/debt/DPP_NI_INE020B08GA0_05032025.zip';

const InstrumentChatBot = ({ isin, bondData, isDark }) => {
  const [chatVisible, setChatVisible] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const chatEndRef = useRef(null);
  const termsheetUrl = useMemo(() => bondData?.termsheetUrl || defaultTermsheetUrl, [
    bondData?.termsheetUrl,
  ]);
  const chatApiEndpoint = process.env.REACT_APP_BOT_URL;

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const appendMessage = (message) => {
    setChatMessages((prev) => [...prev, message]);
  };

  const updateBotMessage = (id, result) => {
    setChatMessages((prev) =>
      prev.map((msg) =>
        msg.id === id
          ? {
              ...msg,
              text:
                result?.status === 'success'
                  ? result.answer || result.message || 'No answer returned'
                  : result?.message || 'Unable to fetch answer',
              isTyping: false,
            }
          : msg
      )
    );
  };

  const sendChatQuery = async ({
    question,
    includeUrl,
    showPlaceholder = true,
    showBotMessage = true,
  }) => {
    const payload =
      includeUrl && question
        ? { termsheet_url: termsheetUrl, question }
        : includeUrl
        ? { termsheet_url: termsheetUrl }
        : question
        ? { question }
        : null;
    if (!payload) return;
    setIsChatLoading(true);
    const placeholderId =
      showPlaceholder && showBotMessage && question ? `bot-${Date.now()}` : null;
    if (placeholderId) {
      appendMessage({ sender: 'bot', text: '', id: placeholderId, isTyping: true });
    }

    try {
      const response = await apiClient.post(`${chatApiEndpoint}/api/query-termsheet`, payload);
      const result = response.data;
      if (placeholderId) {
        updateBotMessage(placeholderId, result);
      } else if (showBotMessage) {
        appendMessage({
          sender: 'bot',
          text:
            result?.status === 'success'
              ? result.answer || result.message || 'No answer returned'
              : result?.message || 'Unable to fetch answer',
        });
      }
    } catch (error) {
      if (placeholderId) {
        updateBotMessage(placeholderId, { status: 'error', message: `Error: ${error.message}` });
      } else if (showBotMessage) {
        appendMessage({ sender: 'bot', text: `Error: ${error.message}` });
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const ensureChatInitialized = async () => {
    if (chatInitialized) return;
    const initialQuestion = 'What is the Issuer Name?';
    await sendChatQuery({
      question: initialQuestion,
      includeUrl: true,
      showPlaceholder: false,
      showBotMessage: false,
    });
    setChatInitialized(true);
  };

  const handleChatToggle = async () => {
    const nextVisible = !chatVisible;
    setChatVisible(nextVisible);
    if (nextVisible) {
      await ensureChatInitialized();
    } else {
      setIsChatExpanded(false);
    }
  };

  const handleChatSubmit = async () => {
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    appendMessage({ sender: 'user', text: trimmed });
    setChatInput('');
    await sendChatQuery({ question: trimmed, includeUrl: false });
  };

  const iconButton = (
    <div
      onClick={handleChatToggle}
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: '#2563eb',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 12px 24px rgba(37, 99, 235, 0.35)',
        cursor: 'pointer',
        zIndex: 1000,
      }}
      aria-label={chatVisible ? 'Collapse chatbot' : 'Open chatbot'}
    >
      {chatVisible ? <DownOutlined style={{ fontSize: '24px' }} /> : <MessageOutlined style={{ fontSize: '24px' }} />}
    </div>
  );

  const chatContainerStyle = isChatExpanded
    ? {
        position: 'fixed',
        right: 0,
        left: 0,
        bottom: 0,
        top: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        background: 'rgba(15, 23, 42, 0.6)',
        zIndex: 1000,
      }
    : {};

  const panelStyle = {
    position: 'fixed',
    right: isChatExpanded ? 0 : 24,
    left: isChatExpanded ? 0 : 'auto',
    bottom: isChatExpanded ? 0 : 96,
    width: isChatExpanded ? '100%' : 340,
    height: isChatExpanded ? '100%' : 'auto',
    maxHeight: isChatExpanded ? '100%' : '70vh',
    background: isDark ? '#0f172a' : '#ffffff',
    borderRadius: isChatExpanded ? 0 : 18,
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    margin: isChatExpanded ? 0 : 0,
  };

  return (
    <>
      {iconButton}
      {chatVisible && (
        <div style={chatContainerStyle}>
          <div style={panelStyle}>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${isDark ? '#1f2937' : '#e5e7eb'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontWeight: 600 }}>Chat with AI</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setIsChatExpanded((prev) => !prev)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isDark ? '#ffffff' : '#4b5563',
                    fontSize: 18,
                    cursor: 'pointer',
                  }}
                  aria-label={isChatExpanded ? 'Exit full screen' : 'Full screen'}
                  title={isChatExpanded ? 'Exit full screen' : 'Full screen'}
                >
                  {isChatExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                </button>
                <button
                  onClick={handleChatToggle}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isDark ? '#ffffff' : '#4b5563',
                    fontSize: 18,
                    cursor: 'pointer',
                  }}
                  aria-label="Close chat"
                >
                  ×
                </button>
              </div>
            </div>
            <div
              style={{
                padding: '12px 16px',
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div
                className={`chat-welcome-card${isChatExpanded ? ' chat-welcome-card-full' : ''}`}
              >
                <div className="chat-welcome-card-header">
                  <strong>Hi 👋 I’m your AI assistant. How can I help you today?</strong>
                  <span className="chat-welcome-divider" />
                </div>
                <div className="chat-welcome-card-body">
                  <span>I can answer questions about the uploaded termsheet and give you deeper insights.</span>
                </div>
                {isChatLoading && chatMessages.length === 0 && (
                  <span className="typing-indicator">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </span>
                )}
              </div>
              {chatMessages.map((msg, index) => (
                <div
                  key={`${msg.sender}-${index}`}
                  style={{
                    alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    background:
                      msg.sender === 'user'
                        ? 'linear-gradient(135deg, #38bdf8, #2563eb)'
                        : isDark
                        ? '#1f2937'
                        : '#f3f4f6',
                    color: msg.sender === 'user' ? '#ffffff' : isDark ? '#e5e7eb' : '#111827',
                    padding: '10px 14px',
                    borderRadius: 12,
                    maxWidth: '100%',
                    whiteSpace: 'pre-wrap',
                    fontSize: 13,
                  }}
                >
                  {msg.isTyping ? (
                    <span className="typing-indicator">
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                    </span>
                  ) : (
                    msg.text
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div
              style={{
                padding: '8px 12px',
                borderTop: `1px solid ${isDark ? '#1f2937' : '#e5e7eb'}`,
                display: 'flex',
                gap: 8,
              }}
            >
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleChatSubmit();
                  }
                }}
                placeholder="Ask a question…"
                style={{
                  flex: 1,
                  borderRadius: 10,
                  border: `1px solid ${isDark ? '#1f2937' : '#d1d5db'}`,
                  padding: '8px 12px',
                  background: isDark ? '#0f172a' : '#ffffff',
                  color: isDark ? '#f8fafc' : '#111827',
                }}
              />
              <button
                onClick={handleChatSubmit}
                disabled={isChatLoading}
                style={{
                  borderRadius: '50%',
                  border: 'none',
                  background: isChatLoading ? '#94a3b8' : '#2563eb',
                  color: '#ffffff',
                  width: 40,
                  height: 40,
                  cursor: isChatLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label="Send question"
              >
                <SendOutlined />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstrumentChatBot;

